import { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { useRepositories } from '../../../infrastructure/http/RepositoryProvider';
import type { LotShare } from '../../../domain/Lot';
import type { PendingQrInvite } from '../../../domain/DeviceShare';

// Con quién está compartido el lote (solo lo ve y lo cambia el dueño).
export function useLotShares(lotId: string, enabled: boolean) {
  const { lots: lotRepo } = useRepositories();
  const [shares, setShares] = useState<LotShare[]>([]);
  const [email, setEmail] = useState('');
  const [level, setLevel] = useState<'view' | 'control'>('view');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingQr, setPendingQr] = useState<Omit<PendingQrInvite, 'id'> | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [qrExpiresAt, setQrExpiresAt] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    lotRepo
      .listShares(lotId)
      .then(({ shares: list, pendingQrInvite }) => {
        if (cancelled) return;
        setShares(list);
        setPendingQr(pendingQrInvite);
      })
      .catch((err) => !cancelled && setError(err instanceof Error ? err.message : 'No se pudo cargar con quién está compartido.'));
    return () => {
      cancelled = true;
    };
  }, [lotId, enabled, lotRepo]);

  const invite = async () => {
    setBusy(true);
    setError(null);
    try {
      const { share } = await lotRepo.share(lotId, email.trim(), level);
      setShares((prev) => [...prev.filter((s) => s.id !== share.id && s.userId !== share.userId), share]);
      setEmail('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo compartir el lote.');
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (shareId: string) => {
    setBusy(true);
    setError(null);
    try {
      await lotRepo.revokeShare(lotId, shareId);
      setShares((prev) => prev.filter((s) => s.id !== shareId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo quitar el acceso.');
    } finally {
      setBusy(false);
    }
  };

  // El mismo link /join que el de una estación: el backend sabe que es de un lote.
  const generateQr = async () => {
    setBusy(true);
    setError(null);
    try {
      const { token, expiresAt } = await lotRepo.createQrInvite(lotId, level);
      setQrDataUrl(await QRCode.toDataURL(`${window.location.origin}/join?token=${token}`, { margin: 1, width: 240 }));
      setQrExpiresAt(expiresAt);
      setPendingQr({ level, expiresAt });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo generar el código QR.');
    } finally {
      setBusy(false);
    }
  };

  return {
    shares,
    email,
    setEmail,
    level,
    setLevel,
    busy,
    error,
    invite,
    revoke,
    pendingQr,
    qrDataUrl,
    setQrDataUrl,
    qrExpiresAt,
    generateQr,
  };
}
